import { useCallback } from 'react'

import { useAccount, useChainId, usePublicClient } from 'wagmi'

import { Hash } from 'types/entities'
import { isEther, bump } from 'utils/crypto'
import { errors } from 'constants/errors'
import { etherNumbers } from 'constants/variables'

import { useCreateTransaction } from '../wallet/useCreateTransaction'

import { ERC20 } from 'abis/ERC20'
import { getWalletClient } from '@wagmi/core'

export function useApproveErc20Estimate() {
  const { address: currentAddress } = useAccount()
  const { estimateContractGas } = usePublicClient()

  return useCallback(
    async (amount: bigint, token: Hash, contractAddress: Hash) => {
      try {
        if (!currentAddress) {
          throw new Error(errors.wallet.WALLET_REQUIRED)
        }
        const params = { abi: ERC20, account: currentAddress, address: token }

        const [valueEstimate, zeroEstimate] = await Promise.allSettled([
          estimateContractGas({ ...params, functionName: 'approve', args: [contractAddress, amount] }),
          estimateContractGas({ ...params, functionName: 'approve', args: [contractAddress, etherNumbers.BG_ZERO] }),
        ])

        if (zeroEstimate.status === 'fulfilled' && valueEstimate.status === 'rejected') {
          throw new Error(errors.token.TWO_STEP_APPROVE)
        }
        return valueEstimate.status === 'fulfilled' ? bump(valueEstimate.value, 10) : etherNumbers.BG_ZERO
      } catch (err) {
        throw err
      }
    },
    [estimateContractGas, currentAddress],
  )
}

export function useCheckAllowanceERC20() {
  const { readContract } = usePublicClient()

  return useCallback(
    async (currentAddress: Hash, tokenAddress: Hash, targetAddress: Hash) => {
      if (!currentAddress || isEther(targetAddress)) {
        return etherNumbers.BG_ZERO
      }
      const allowance = await readContract({
        abi: ERC20,
        address: tokenAddress,
        account: currentAddress,
        functionName: 'allowance',
        args: [currentAddress, targetAddress],
      })

      return allowance as bigint
    },
    [readContract],
  )
}

export function useApproveERC20() {
  const chainId = useChainId()
  const { address: currentAddress } = useAccount()

  const createTransaction = useCreateTransaction()
  const approveErc20Estimate = useApproveErc20Estimate()

  return useCallback(
    async (amount: bigint, token: Hash, contractAddress: Hash): Promise<Hash | null> => {
      try {
        const walletClient = await getWalletClient({ chainId })
        if (!walletClient) {
          throw new Error(errors.wallet.WALLET_REQUIRED)
        }

        const hash = await walletClient.writeContract({
          abi: ERC20,
          address: token,
          functionName: 'approve',
          args: [contractAddress, amount],
        })

        return hash
      } catch (err) {
        throw err
      }
    },
    [currentAddress, createTransaction, approveErc20Estimate, chainId],
  )
}
